if (!customElements.get('spooky-discount-promo')) {
  class SpookyDiscountPromo extends HTMLElement {
    constructor() {
      super();
      this.variantChangeUnsub = undefined;
      this.cartUpdateUnsub = undefined;
      this.basePriceCents = 0;
      this.discountPercent = 15;
      this.discountCode = '';
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.discountPercent = parseInt(this.dataset.discountPercent, 10) || 15;
      this.discountCode = this.dataset.discountCode || '';
      this.basePriceCents = parseInt(this.dataset.variantPrice, 10) || 0;

      this.priceEl = this.querySelector('[data-spooky-price]');
      this.compareEl = this.querySelector('[data-spooky-compare]');
      this.saveEl = this.querySelector('[data-spooky-save]');
      this.applyBtn = this.querySelector('[data-spooky-apply]');
      this.applyLabelEl = this.querySelector('.spooky-discount__apply-label');
      this.applyDoneEl = this.querySelector('.spooky-discount__apply-done');
      this.toastEl = this.querySelector('[data-spooky-error]');
      this.errorMessage =
        this.dataset.errorMessage ||
        FoxTheme.cartStrings?.applyDiscountError ||
        'Could not apply discount.';
      this.appliedMessage = this.dataset.appliedMessage || 'Promocode applied';
      this.toastHideTimer = undefined;
      this.copyHideTimer = undefined;
      this.copiedMessage = this.dataset.copiedMessage || 'Copied';

      if (this.sectionId) {
        this.variantChangeUnsub = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      if (this.applyBtn) {
        this.cartUpdateUnsub = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          ({ cart }) => this.updateAppliedFromCart(cart)
        );
        this.syncAppliedStateFromCart();
      }

      this.applyBtn?.addEventListener('click', () => this.applyDiscountCode());
      this.querySelectorAll('[data-spooky-copy-code]').forEach((button) => {
        button.dataset.defaultAriaLabel = button.getAttribute('aria-label') || '';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.copyPromoCode(button);
        });
      });
      this.updatePrices(this.basePriceCents);
    }

    disconnectedCallback() {
      if (this.variantChangeUnsub) {
        this.variantChangeUnsub();
      }
      if (this.cartUpdateUnsub) {
        this.cartUpdateUnsub();
      }
    }

    normalizeCode(value) {
      return String(value || '')
        .trim()
        .toUpperCase();
    }

    onVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId || !data.variant) {
        return;
      }

      this.updatePrices(data.variant.price);
    }

    discountedCents(cents) {
      const multiplier = 100 - this.discountPercent;
      return Math.round((cents * multiplier) / 100);
    }

    formatMoney(cents) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }

    updatePrices(cents) {
      if (!cents || !this.priceEl) {
        return;
      }

      this.basePriceCents = cents;
      const discounted = this.discountedCents(cents);

      this.priceEl.innerHTML = this.formatMoney(discounted);

      if (this.compareEl) {
        this.compareEl.innerHTML = this.formatMoney(cents);
      }

      if (this.saveEl) {
        this.saveEl.textContent = this.formatMoneyPlain(cents - discounted);
      }
    }

    formatMoneyPlain(cents) {
      const formatted = this.formatMoney(cents);
      return formatted.replace(/<[^>]*>/g, '').trim();
    }

    async copyPromoCode(button) {
      const code = this.discountCode;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const helper = document.createElement('textarea');
          helper.value = code;
          helper.setAttribute('readonly', '');
          helper.style.position = 'absolute';
          helper.style.left = '-9999px';
          document.body.appendChild(helper);
          helper.select();
          document.execCommand('copy');
          document.body.removeChild(helper);
        }

        this.querySelectorAll('[data-spooky-copy-code].is-copied').forEach((node) => {
          node.classList.remove('is-copied');
        });

        button.classList.add('is-copied');
        button.setAttribute('aria-label', this.copiedMessage);

        if (this.copyHideTimer) {
          clearTimeout(this.copyHideTimer);
        }

        this.copyHideTimer = setTimeout(() => {
          button.classList.remove('is-copied');
          button.setAttribute(
            'aria-label',
            button.dataset.defaultAriaLabel || `Copy promocode ${code}`
          );
        }, 2000);
      } catch (error) {
        console.error(error);
      }
    }

    getExistingDiscountsFromDom() {
      const discounts = [];
      const container = document.querySelector('.cart__discounts');

      if (container) {
        container.querySelectorAll('.discount').forEach((item) => {
          if (item.dataset.discountCode) {
            discounts.push(item.dataset.discountCode);
          }
        });
      }

      return discounts;
    }

    cartHasActivePromoCode(cart) {
      if (!cart) {
        return false;
      }

      const target = this.normalizeCode(this.discountCode);

      const cartLevel = cart.cart_level_discount_applications || [];
      for (const application of cartLevel) {
        if (
          application.type === 'discount_code' &&
          this.normalizeCode(application.title) === target
        ) {
          return true;
        }
      }

      for (const item of cart.items || []) {
        for (const allocation of item.line_level_discount_allocations || []) {
          const application = allocation.discount_application;
          if (
            application?.type === 'discount_code' &&
            this.normalizeCode(application.title) === target
          ) {
            return true;
          }
        }
      }

      return false;
    }

    isCodeApplicableInUpdateResponse(parsedState, code) {
      const entries = parsedState?.discount_codes;
      if (!Array.isArray(entries)) {
        return false;
      }

      const target = this.normalizeCode(code);
      const entry = entries.find((discount) => this.normalizeCode(discount.code) === target);

      return entry?.applicable === true;
    }

    showToast(message, type = 'error') {
      const text = message || '';

      if (this.toastHideTimer) {
        clearTimeout(this.toastHideTimer);
        this.toastHideTimer = undefined;
      }

      if (!this.toastEl) {
        if (text && type === 'error') {
          alert(text);
        }
        return;
      }

      if (!text) {
        this.toastEl.textContent = '';
        this.toastEl.classList.add('hidden');
        this.toastEl.classList.remove('spooky-discount__toast--success');
        return;
      }

      const shortText = text.length > 72 ? `${text.slice(0, 69)}…` : text;

      this.toastEl.textContent = shortText;
      this.toastEl.classList.toggle('spooky-discount__toast--success', type === 'success');
      this.toastEl.classList.remove('hidden');

      const hideAfter = type === 'success' ? 3200 : 4500;
      this.toastHideTimer = setTimeout(() => this.showToast(''), hideAfter);
    }

    markApplied({ showToast = false } = {}) {
      if (!this.applyBtn) {
        return;
      }

      this.applyBtn.classList.add('is-applied');
      this.applyBtn.setAttribute('aria-disabled', 'true');
      this.applyLabelEl?.classList.add('hidden');
      this.applyDoneEl?.classList.remove('hidden');

      if (showToast) {
        this.showToast(this.appliedMessage, 'success');
      }
    }

    markNotApplied() {
      if (!this.applyBtn) {
        return;
      }

      this.applyBtn.classList.remove('is-applied');
      this.applyBtn.removeAttribute('aria-disabled');
      this.applyLabelEl?.classList.remove('hidden');
      this.applyDoneEl?.classList.add('hidden');
    }

    updateAppliedFromCart(cart) {
      if (!this.applyBtn) {
        return;
      }

      if (this.cartHasActivePromoCode(cart)) {
        this.markApplied();
      } else {
        this.markNotApplied();
      }
    }

    async syncAppliedStateFromCart() {
      try {
        const cart = await fetch(`${FoxTheme.routes.cart_url}`, {
          ...FoxTheme.utils.fetchConfig(),
        }).then((response) => response.json());
        this.updateAppliedFromCart(cart);
      } catch (error) {
        console.error(error);
      }
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(this.applyBtn);
      }
    }

    async applyDiscountCode() {
      if (!this.applyBtn || this.applyBtn.classList.contains('is-applied')) {
        return;
      }

      const code = this.discountCode;
      const normalizedCode = this.normalizeCode(code);

      try {
        const cartBefore = await fetch(`${FoxTheme.routes.cart_url}`, {
          ...FoxTheme.utils.fetchConfig(),
        }).then((response) => response.json());

        if (this.cartHasActivePromoCode(cartBefore)) {
          this.markApplied({ showToast: true });
          this.openCartDrawer();
          return;
        }
      } catch (error) {
        console.error(error);
      }

      const discounts = this.getExistingDiscountsFromDom().map((item) => String(item).trim());
      const hasCodeInDom = discounts.some((item) => this.normalizeCode(item) === normalizedCode);

      if (!hasCodeInDom) {
        discounts.push(code);
      }

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('sections', sectionsToBundle);
      formData.append('sections_url', window.location.pathname);
      formData.append('discount', discounts.join(','));

      config.body = formData;

      this.applyBtn.classList.add('is-loading');
      this.showToast('');

      try {
        const parsedState = await fetch(FoxTheme.routes.cart_update_url, config).then((response) =>
          response.json()
        );

        const failed = parsedState.discount_codes?.find(
          (discount) =>
            this.normalizeCode(discount.code) === normalizedCode && discount.applicable === false
        );

        if (failed) {
          this.showToast(this.errorMessage, 'error');
          return;
        }

        const cartJson = await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() }).then(
          (response) => response.json()
        );
        cartJson.sections = parsedState.sections;

        const isActive =
          this.cartHasActivePromoCode(cartJson) || this.isCodeApplicableInUpdateResponse(parsedState, code);

        if (!isActive) {
          this.showToast(this.errorMessage, 'error');
          return;
        }

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        this.markApplied({ showToast: true });
        this.openCartDrawer();
      } catch (error) {
        console.error(error);
        this.showToast(this.errorMessage, 'error');
      } finally {
        this.applyBtn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('spooky-discount-promo', SpookyDiscountPromo);
}
